import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Globe, Menu, X } from 'lucide-react';

function Header() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'fr' : 'en';
    i18n.changeLanguage(newLang);
  };

  const isActive = (path) => location.pathname === path;
  const isHome = location.pathname === '/';

  const logoSrc = 'https://customer-assets.emergentagent.com/job_f2e1ed22-1a5e-4b46-a0b5-df4af0078423/artifacts/yu0958al_Emblem%20Brown.png';

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      scrolled || !isHome 
        ? 'bg-warm-white/95 backdrop-blur-xl shadow-sm' 
        : 'bg-transparent'
    }`} data-testid="header">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3" data-testid="logo-link">
            <img src={logoSrc} alt="Casapooch" className="h-12 w-auto" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8" data-testid="desktop-nav">
            {[
              { path: '/', key: 'nav.home', testId: 'nav-home' },
              { path: '/about', key: 'nav.about', testId: 'nav-about' },
              { path: '/services', key: 'nav.services', testId: 'nav-services' },
              { path: '/blog', key: 'nav.blog', testId: 'nav-blog' },
              { path: '/events', key: 'nav.events', testId: 'nav-events' },
              { path: '/contact', key: 'nav.contact', testId: 'nav-contact' },
            ].map(item => (
              <Link
                key={item.path}
                to={item.path}
                className={`nav-link transition-colors ${
                  isActive(item.path) 
                    ? 'text-primary' 
                    : (!scrolled && isHome) ? 'text-white/90 hover:text-white' : 'text-charcoal'
                }`}
                data-testid={item.testId}
              >
                {t(item.key)}
              </Link>
            ))}
          </nav>

          {/* Right Section */}
          <div className="flex items-center space-x-4">
            <button
              onClick={toggleLanguage}
              className={`flex items-center space-x-2 px-4 py-2 rounded-full border transition-colors duration-300 ${
                (!scrolled && isHome) 
                  ? 'border-white/50 text-white hover:bg-white/10' 
                  : 'border-primary text-primary hover:bg-accent/20'
              }`}
              data-testid="language-toggle"
              aria-label={t('nav.langToggle')}
              title={t('nav.langToggle')}
            >
              <Globe className="w-4 h-4" />
              <span className="font-medium text-sm">{i18n.language.toUpperCase()}</span>
            </button>

            <Link 
              to="/book" 
              className={`hidden md:block px-8 py-3 rounded-full transition-colors duration-300 font-medium ${
                (!scrolled && isHome)
                  ? 'bg-white/20 backdrop-blur-sm text-white hover:bg-white/30 border border-white/30'
                  : 'bg-primary hover:bg-charcoal text-white'
              }`}
              data-testid="book-now-btn"
            >
              {t('nav.bookNow')}
            </Link>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2"
              aria-label={isMobileMenuOpen ? t('nav.closeMenu') : t('nav.openMenu')}
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? (
                <X className={`w-6 h-6 ${(!scrolled && isHome) ? 'text-white' : 'text-charcoal'}`} />
              ) : (
                <Menu className={`w-6 h-6 ${(!scrolled && isHome) ? 'text-white' : 'text-charcoal'}`} />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <nav className="md:hidden mt-4 pb-4 pt-4 bg-warm-white/95 backdrop-blur-xl rounded-2xl px-4 -mx-2" data-testid="mobile-nav">
            <div className="flex flex-col space-y-4">
              {[
                { path: '/', key: 'nav.home' },
                { path: '/about', key: 'nav.about' },
                { path: '/services', key: 'nav.services' },
                { path: '/blog', key: 'nav.blog' },
                { path: '/events', key: 'nav.events' },
                { path: '/contact', key: 'nav.contact' },
              ].map(item => (
                <Link 
                  key={item.path}
                  to={item.path} 
                  className="text-charcoal hover:text-primary transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {t(item.key)}
                </Link>
              ))}
              <Link 
                to="/book" 
                className="px-8 py-3 rounded-full bg-primary hover:bg-charcoal text-white transition-colors duration-300 font-medium text-center"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t('nav.bookNow')}
              </Link>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
}

export default Header;